import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api/client';
import { useUserContext } from '@/lib/auth/useUserContext';
import { useViewScope } from '@/lib/scope/ViewScope';
import { formatShortDate } from '@/lib/format';

type Category = 'equipment' | 'safety' | 'customer' | 'security' | 'food' | 'other';
type Severity = 'low' | 'medium' | 'high' | 'critical';
type Status = 'open' | 'in_progress' | 'resolved' | 'closed';

interface IncidentSummary {
  id: string;
  storeId: string;
  title: string;
  category: Category;
  severity: Severity;
  status: Status;
  description: string | null;
  openedById: string;
  openedByTier: 'manager' | 'store_ops';
  closedAt: string | null;
  createdAt: string;
}

interface IncidentUpdate {
  id: string;
  body: string;
  authorId: string;
  authorTier: 'manager' | 'store_ops';
  statusChange: Status | null;
  createdAt: string;
}

interface IncidentDetail extends IncidentSummary {
  updates: IncidentUpdate[];
}

export function Incidents() {
  const { scopedStores } = useUserContext();
  const { storeLabel, inScope } = useViewScope();
  const [rows, setRows] = useState<IncidentSummary[]>([]);
  const visibleRows = rows.filter((r) => inScope(r.storeId));
  const [filter, setFilter] = useState<'all' | Status>('open');
  const [createOpen, setCreateOpen] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    const p = new URLSearchParams();
    if (filter !== 'all') p.set('status', filter);
    apiFetch<{ incidents: IncidentSummary[] }>(`/incidents?${p.toString()}`)
      .then((r) => setRows(r.incidents))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => {
    reload();
  }, [reload]);

  const severityColor = (s: Severity) =>
    s === 'critical'
      ? 'text-destructive'
      : s === 'high'
        ? 'text-amber-700'
        : s === 'medium'
          ? 'text-amber-600'
          : 'text-muted-foreground';

  return (
    <div className="min-h-screen p-6 max-w-4xl mx-auto">
      <header className="flex items-center justify-between border-b border-border pb-4 mb-4">
        <h1 className="text-2xl font-semibold">Incidents</h1>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium"
        >
          Open incident
        </button>
      </header>

      <div className="flex items-center gap-3 mb-4">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as typeof filter)}
          className="border border-border rounded-md px-2 py-1 text-sm"
        >
          <option value="all">all</option>
          <option value="open">open</option>
          <option value="in_progress">in progress</option>
          <option value="resolved">resolved</option>
          <option value="closed">closed</option>
        </select>
      </div>

      {error ? <p className="text-sm text-destructive mb-3">{error}</p> : null}
      {loading && rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : null}

      <ul className="space-y-2">
        {visibleRows.map((r) => (
          <li
            key={r.id}
            className="border border-border rounded-md p-3 bg-card hover:bg-muted cursor-pointer"
            onClick={() => setActiveId(r.id)}
          >
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">{r.title}</div>
              <span className={`text-xs ${severityColor(r.severity)}`}>
                {r.severity.toUpperCase()}
              </span>
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              {storeLabel(r.storeId)} · {r.category} · {r.status} · opened{' '}
              {formatShortDate(r.createdAt)}
            </div>
          </li>
        ))}
        {!loading && visibleRows.length === 0 ? (
          <li className="py-6 text-center text-muted-foreground">
            No incidents{rows.length > 0 ? ' in the current view scope' : ''}.
          </li>
        ) : null}
      </ul>

      {createOpen ? (
        <CreateDialog
          scopedStores={scopedStores.filter(inScope)}
          storeLabel={storeLabel}
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            reload();
          }}
        />
      ) : null}
      {activeId ? (
        <DetailDialog
          incidentId={activeId}
          storeLabel={storeLabel}
          onClose={() => setActiveId(null)}
          onChanged={() => {
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

function CreateDialog({
  scopedStores,
  storeLabel,
  onClose,
  onCreated,
}: {
  scopedStores: string[];
  storeLabel: (id: string) => string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [storeId, setStoreId] = useState(scopedStores[0] ?? '');
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState<Category>('other');
  const [severity, setSeverity] = useState<Severity>('medium');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    if (!title.trim()) {
      setErr('title required');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await apiFetch('/incidents', {
        method: 'POST',
        body: JSON.stringify({
          storeId,
          title,
          category,
          severity,
          description: description || undefined,
        }),
      });
      onCreated();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'submit failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-6 z-50">
      <div className="bg-card rounded-xl p-6 w-full max-w-md space-y-3">
        <h3 className="text-lg font-semibold">Open incident</h3>
        <label className="block text-sm">
          Store
          <select
            value={storeId}
            onChange={(e) => setStoreId(e.target.value)}
            className="mt-1 w-full border border-border rounded-md px-2 py-1"
          >
            {scopedStores.map((s) => (
              <option key={s} value={s}>
                {storeLabel(s)}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-sm">
          Title
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="mt-1 w-full border border-border rounded-md px-2 py-1"
          />
        </label>
        <div className="grid grid-cols-2 gap-2">
          <label className="block text-sm">
            Category
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as Category)}
              className="mt-1 w-full border border-border rounded-md px-2 py-1"
            >
              {['equipment', 'safety', 'customer', 'security', 'food', 'other'].map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm">
            Severity
            <select
              value={severity}
              onChange={(e) => setSeverity(e.target.value as Severity)}
              className="mt-1 w-full border border-border rounded-md px-2 py-1"
            >
              {['low', 'medium', 'high', 'critical'].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="block text-sm">
          Description
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="mt-1 w-full border border-border rounded-md px-2 py-1 min-h-[80px]"
          />
        </label>
        {err ? <p className="text-sm text-destructive">{err}</p> : null}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-1 text-sm">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={busy || !storeId}
            className="px-3 py-1 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-60"
          >
            {busy ? 'Submitting…' : 'Open'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DetailDialog({
  incidentId,
  storeLabel,
  onClose,
  onChanged,
}: {
  incidentId: string;
  storeLabel: (id: string) => string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [detail, setDetail] = useState<IncidentDetail | null>(null);
  const [body, setBody] = useState('');
  const [statusChange, setStatusChange] = useState<Status | ''>('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(() => {
    apiFetch<{ incident: IncidentDetail }>(`/incidents/${incidentId}`)
      .then((r) => setDetail(r.incident))
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)));
  }, [incidentId]);

  useEffect(() => {
    load();
  }, [load]);

  async function append() {
    if (!body.trim() && !statusChange) {
      setErr('add a comment or change status');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await apiFetch(`/incidents/${incidentId}/updates`, {
        method: 'POST',
        body: JSON.stringify({
          body: body || '(status change)',
          statusChange: statusChange || undefined,
        }),
      });
      setBody('');
      setStatusChange('');
      load();
      onChanged();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'update failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-6 z-50 overflow-y-auto">
      <div className="bg-card rounded-xl p-6 w-full max-w-2xl space-y-3 my-8">
        {!detail ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold">{detail.title}</h3>
                <p className="text-xs text-muted-foreground">
                  {storeLabel(detail.storeId)} · {detail.category} · severity {detail.severity} ·
                  status <strong>{detail.status}</strong> · opened{' '}
                  {formatShortDate(detail.createdAt)}
                </p>
              </div>
              <button type="button" onClick={onClose} className="text-sm">
                Close
              </button>
            </div>
            {detail.description ? (
              <p className="text-sm whitespace-pre-wrap">{detail.description}</p>
            ) : null}

            <div className="border-t border-border pt-3">
              <div className="text-sm font-medium mb-2">Timeline</div>
              <ul className="space-y-2">
                {detail.updates.map((u) => (
                  <li key={u.id} className="text-sm border-l-2 border-border pl-3">
                    <p className="whitespace-pre-wrap">{u.body}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatShortDate(u.createdAt)} · {u.authorTier} {u.authorId.slice(0, 8)}…
                      {u.statusChange ? <span> · status → {u.statusChange}</span> : null}
                    </p>
                  </li>
                ))}
                {detail.updates.length === 0 ? (
                  <li className="text-xs text-muted-foreground italic">No updates yet.</li>
                ) : null}
              </ul>
            </div>

            {detail.status !== 'closed' ? (
              <div className="border-t border-border pt-3 space-y-2">
                <label className="block text-sm">
                  Add update
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    className="mt-1 w-full border border-border rounded-md px-2 py-1 min-h-[60px]"
                  />
                </label>
                <label className="block text-sm">
                  Change status (optional)
                  <select
                    value={statusChange}
                    onChange={(e) => setStatusChange(e.target.value as Status | '')}
                    className="mt-1 w-full border border-border rounded-md px-2 py-1"
                  >
                    <option value="">— no change —</option>
                    <option value="in_progress">in_progress</option>
                    <option value="resolved">resolved</option>
                    <option value="closed">closed</option>
                  </select>
                </label>
                {err ? <p className="text-sm text-destructive">{err}</p> : null}
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => void append()}
                    disabled={busy}
                    className="px-3 py-1 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-60"
                  >
                    {busy ? 'Saving…' : 'Save update'}
                  </button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
